import { AppData, Utils } from '@lightningjs/sdk'
import { AppSetup } from './app-setup'
import { PLATFORM, PLAYLIST_SWITCHING_STRATEGY } from './enum'
import { Helper } from './helper'
import { log } from 'console'

export type MultiLangDescription = {
  language: string;
  file_path: string; // contains the actual description text, despite the field name
};

export type MultiLangCC = {
  language: string;
  file_path: string; // contains the file path for respective languages.
  aicc?: boolean;
}

export type ImageAdListInfo = {
  videoInterval: number
  imgAd: string
}

export type SelectScreenOptionInfo = {
  blockItem: boolean
  desc: string
  img: string
  imgSelect: string
  l1_mapping: number
  title: string
  url: string
}

export type SelectScreenInfo = {
  focusimg: string
  fontcolor: string
  liveTvFeedUrl: string
  liveTvImg: string
  optimgh: number
  optimgw: number
  options: SelectScreenOptionInfo[]
  promodesc: string
  promotext: string
  screenbgcolor: string
  scrollimgleft: string
  scrollimgright: string
  selectimg: string
  skipimg: string
  skipimgSelect: string
  textFocusColor: string
  textSize: number
  type: string
  bgImg: string
  noOfRow: number
  selectedRow: number
  selctedColumn: number
}

export type AppConfig = {
  appStoreId: string | undefined
  autoDeeplink: boolean
  channels: Channel[]
  dataRefreshInterval: number
  displayParentalScreen: boolean
  displaySelectScreen: boolean
  enableAutoPlayback: boolean
  autoplayTimer: number
  mlpAutoplay: boolean
  heroImageAutoplay: boolean
  enableBlockFeature: boolean
  enableCuePts: boolean
  enableMixedGrid: boolean
  enableDisplayNumber:boolean
  enableDynamicThumbnail: boolean
  enableMultiLangDescription: boolean
  enableNotifications: boolean
  enableSelectScreen: false
  enableStartupLogin: boolean
  enableVideoResume: boolean
  enablesuggestions: boolean
  enableLikeDislike: boolean
  enableTrailer: boolean
  genre: string
  gridstyle: string
  dltype: string
  imageAdList: ImageAdListInfo
  isunifiedapp: boolean
  notificationInterval: number
  playlistSwitchingStrategy: string
  resumeVidMinDuration: number
  selectscreen: SelectScreenInfo | undefined
  showStarRatingOnCallout: boolean
  showadvancedsearch: boolean
  showvideoinfo: boolean
  site_name: string
  unifiedappIndex: number
  unifiedappurl: string
  us_privacy: string
  comcastAppBundleId: any
  coxAppBundleId: any
  ageRatingColor:string
  titleColor:string
  header_logo: string
  lgAppstoreId: string
  firetvAppstoreId: string
  vizioAppId: string
  vizioSourceId: string
  samsungAppId: string
  samsungAppStoreId: string
  samsungAppStoreUrl: string
  square_icon: string
  firstmidrollsize: number
  likeImg: string
  likeSelectImg: string
  dislikeImg: string
  dislikeSelectImg: string
  loveitImg: string
  loveitSelectImg: string
  carouselTimer: string
  displayNewOverlay: boolean
  newOverlayImage: string
  newOverlayImgPortrait: string
  newEpisodeOverlayImage: string
  newEpisodeOverlayImgPortrait: string
  leavingSoonOverlayImage: string
  leavingSoonOverlayImgPortrait: string
  topTenOverlayImage: string
  topTenOverlayImgPortrait: string
  enableContentLogo: boolean
  userType: string
  enableMultipleBanner: boolean
}

export type UserConfig = {
  cityName: string
  countryCode: string
  regionName: string
  sessionid: string
  subscriptionStatus: boolean
  uid: string
  username: string
}

export type BrandingConfig = {
  description: string
  header_logo: string
  iptv_background: string
  iptv_logo: string
  iptv_pagetitle: string
}

export type LayoutConfig = {
  bgcolor: string
  enableMenuDescOnCallout: string
  griddescriptionvisible: string
  gridfocuscolor: string
  playerTextColor: string
  playerTextSize: string
  rowtofocus: string
  searchbgcolor: string
  textSize: number
  textcolor: string
  textfocuscolor: string
  topInfoAreaColor: string
  topInfoAreaTextcolor: string
  topInfoAreaVisible: boolean
  menuBgOpacityOpen: number | undefined
  menuBgOpacityClosed: number | undefined,
  menuLineOpacityClosed: number | undefined,
  menuLineOpacityOpen: number | undefined,
  optionsIcon: string,
  optionsIconSelect: string
}

export type AdInfo = {
  name: string
  priority: number
  provider: string
  rowId: string
  type: string
  url: string
}

export type BugAdsInfo = {
  adList: AdInfo
  bugadInterval: number
  bugadduration: number
  bugadenabled: boolean
  bugadrepeatinterval: number
  bugadstartinterval: number
  firstbugadinterval: number
}

export type PauseAdsInfo = {
  adList: AdInfo
  pauseadduration: number
}
const PAUSE_AD_DEFAULT_DURATION = 15

export type AdSdkConfig = {
  adActivation: any
  adInterval: number
  adProbability: number
  bugAds: BugAdsInfo
  pauseAds: PauseAdsInfo
  enableMidAd: boolean
  firstadInterval: number
  midAdRepeatMode: boolean
  midAdTimeInterval: number
  midrollsize: number
  nonmidrollsize: number
  //providers: AdInfo
  providers: any
  enableCuePts: boolean
}

export type Channel = {
  actionkey: string
  actiontype: string
  bannerad_image: string
  multiplebannerad_image: string
  bg_image: string
  cg_image: string
  moreBgImage: string
  blockItem: boolean
  category_id: string
  content_type: string
  description: string
  feed_type: string
  name: string
  isKids: boolean
  isLivetv: boolean
  url: string
  root_icon: string
  root_icon_select: string
  moreImage: string
  moreSelectImage: string
  search_root_icon: string
	search_root_icon_select: string
}

export class AppApiConfig {
  appConfig!: AppConfig
  userConfig!: UserConfig
  appBrandConfig!: BrandingConfig
  appLayoutConfig!: LayoutConfig
  adsdkConfig!: AdSdkConfig

  init(data: any) {
    const { appConfig, platform } = AppData as AppSetup
    this.appConfig = {
      appStoreId: '',
      lgAppstoreId: data.lgAppstoreId || '',
      firetvAppstoreId: data.firetvAppstoreId || '',
      vizioAppId: data.vizioAppId || '',
      vizioSourceId: data.vizioSourceId || '',
      samsungAppId:  data.samsungAppId || '',
      samsungAppStoreId: data.samsungAppStoreId || '',
      samsungAppStoreUrl: data.samsungAppStoreUrl || '',
      autoDeeplink: Helper.toBoolean(data.autoDeeplink, true),
      channels: this._getRootCategories(data),
      dataRefreshInterval: data.dataRefreshInterval,
      enableLikeDislike: Helper.toBoolean(data.enableLikeDislike, true),
      displayParentalScreen: Helper.toBoolean(data.displayParentalScreen, false),
      displaySelectScreen: Helper.toBoolean(data.displaySelectScreen, false),
      enableAutoPlayback: Helper.toBoolean(data.enableAutoPlayback, true),
      firstmidrollsize: data.firstmidrollsize,
      autoplayTimer: Helper.toNumber(data.autoplayTimer, 5),
      mlpAutoplay: Helper.toBoolean(data.mlpAutoplay, false),
      heroImageAutoplay: Helper.toBoolean(data.heroImageAutoplay, false),
      firstmidrollsize: data.firstmidrollsize,
      enableBlockFeature: Helper.toBoolean(data.enableBlockFeature, false),
      enableCuePts: Helper.toBoolean(data.enableCuePts, true),
      enableMixedGrid: Helper.toBoolean(data.enableMixedGrid, true),
      enableDisplayNumber:Helper.toBoolean(data.enableDisplayNumber,true),
      enableDynamicThumbnail: Helper.toBoolean(data.enableDynamicThumbnail, false),
      enableMultiLangDescription: Helper.toBoolean(data.enableMultiLangDescription, false),
      enableNotifications: data.enableNotifications,
      enableSelectScreen: false,
      enableStartupLogin: Helper.toBoolean(data.enableAutoPlayback, true),
      enableVideoResume: Helper.toBoolean(data.enableAutoPlayback, true),
      enablesuggestions: Helper.toBoolean(data.enableAutoPlayback, true),
      enableContentLogo:Helper.toBoolean(data.enableContentLogo, true),
      enableTrailer: Helper.toBoolean(data.enableTrailer, false),
      loggerUrl: (data.loggerUrl) ? data.loggerUrl : 'https://log.ifood.tv/',
      genre: data.genre,
      gridstyle: data.gridstyle,
      dltype: data.dltype,
      imageAdList: this._getImageAdList(data),
      isunifiedapp: Helper.toBoolean(data.enableAutoPlayback, true),
      notificationInterval: data.notificationInterval,
      playlistSwitchingStrategy: this._getPlaylistStrategy(data.playlistSwitchingStrategy),
      resumeVidMinDuration: data.resumeVidMinDuration,
      selectscreen: this._getSelectScreen(data),
      showStarRatingOnCallout: Helper.toBoolean(data.enableAutoPlayback, true),
      showadvancedsearch: Helper.toBoolean(data.enableAutoPlayback, true),
      showvideoinfo: Helper.toBoolean(data.enableAutoPlayback, true),
      site_name: data.site_name,
      unifiedappIndex: data.undefined,
      unifiedappurl: data.unifiedappurl,
      us_privacy: data.us_privacy,
      comcastAppBundleId: data.comcastAppBundleId,
      coxAppBundleId: data.coxAppBundleId,
      ageRatingColor: data.ageRatingColor,
      titleColor: data.titleColor,
      header_logo: data.header_logo,
      square_icon: data.squareIcon,
      likeImg: data.likeImg,
      likeSelectImg: data.likeSelectImg,
      dislikeImg: data.dislikeImg,
      dislikeSelectImg: data.dislikeSelectImg,
      loveitImg: data.loveitImg,
      loveitSelectImg: data.loveitSelectImg,
      carouselTimer: data.carouselTimer,
      displayNewOverlay: Helper.toBoolean(data.displayNewOverlay, false),
      newOverlayImage: data.newOverlayImage,
      newOverlayImgPortrait: data.newOverlayImgPortrait,
      newEpisodeOverlayImage: data.newEpisodeOverlayImage,
      newEpisodeOverlayImgPortrait: data.newEpisodeOverlayImgPortrait,
      leavingSoonOverlayImage: data.leavingSoonOverlayImage,
      leavingSoonOverlayImgPortrait: data.leavingSoonOverlayImgPortrait,
      topTenOverlayImage: data.topTenOverlayImage,
      topTenOverlayImgPortrait: data.topTenOverlayImgPortrait,
      userType: (data.userType && data.userType!=='') ? data.userType : 'existing' ,
      enableMultipleBanner: Helper.toBoolean(data.enableMultipleBanner, false)
    }
    this.userConfig = {
      cityName: data.cityName,
      countryCode: data.countryCode,
      regionName: data.regionName,
      sessionid: data.sessionid,
      subscriptionStatus: Helper.toBoolean(data.subscriptionStatus, true),
      uid: data.uid,
      username: data.username,
    }
    this.appBrandConfig = {
      header_logo: data.header_logo,
      iptv_logo: data.header_logo,
      iptv_background: data.iptv_background,
      iptv_pagetitle: data.iptv_pagetitle,
      description: data.description
    }
    this.appLayoutConfig = {
      textSize: data.layout.textSize,
      textcolor: data.layout.textcolor,
      textfocuscolor:data.layout.textfocuscolor,
      gridfocuscolor: data.layout.textfocuscolor, //setting grid focus color to textfocus color where it is been used for focus color.
      bgcolor: data.layout.bgcolor,
      menuBgOpacityOpen: Helper.toNumber(data.menuBgOpacityOpen,90),
      menuBgOpacityClosed: Helper.toNumber(data.menuBgOpacityOpen,90), // currently not supporting the menuBgOpacityClose as it interfears with the menu items.
      menuLineOpacityClosed: Helper.toNumber(data.menuLineOpacityOpen,20), // currently not supporting the menuBgOpacityClose as it interfears with the menu items.
      menuLineOpacityOpen: Helper.toNumber(data.menuLineOpacityOpen,20),
      optionsIcon: platform.getOptionsIconPath(data.optionsIcon),
      optionsIconSelect: platform.getOptionsIconPath(data.optionsIcon) // Utils.asset('/images/option_i.png')
    }
    this.adsdkConfig = {};
    this.adsdkConfig.bugAds = {} as BugAdsInfo;
    this.adsdkConfig.pauseAds = {} as PauseAdsInfo;
    if (data.adsdk !== undefined && data.adsdk.adActivation !== undefined) {
      this.adsdkConfig.adActivation = data.adsdk.adActivation;
      if (this.adsdkConfig.adActivation == true || this.adsdkConfig.adActivation == "true") {
        this.adsdkConfig.enableCuePts = data.enableCuePts;
        this.adsdkConfig.enableMidAd = data.adsdk.enableMidAd;
        this.adsdkConfig.firstadInterval = data.adsdk.firstadInterval;
        this.adsdkConfig.adInterval = data.adsdk.adInterval;
        this.adsdkConfig.midAdTimeInterval = data.adsdk.midAdTimeInterval;
        this.adsdkConfig.adProbability = data.adsdk.adProbability;
        this.adsdkConfig.midAdRepeatMode = data.adsdk.midAdRepeatMode;
        this.adsdkConfig.midrollsize = data.adsdk.midrollsize;
        this.adsdkConfig.nonmidrollsize = data.adsdk.nonmidrollsize;
        if (data.adsdk.adsdklist !== undefined) {
          this.adsdkConfig.providers = [];
          var n = 0;
          Object.entries(data.adsdk.adsdklist).forEach(([key, value]) => {
            this.adsdkConfig.providers[n] = [];
            this.adsdkConfig.providers[n].name = value.name;
            this.adsdkConfig.providers[n].provider = value.provider;
            this.adsdkConfig.providers[n].priority = value.priority;
            this.adsdkConfig.providers[n].type = value.type;
            this.adsdkConfig.providers[n].url = value.url;
            if (value.type.toLowerCase() == "popup") {
              this.adsdkConfig.bugAds.adList = this.adsdkConfig.providers[n];
            }
            // Pause ads are served in the same adsdklist under type "Pause".
            if (this.adsdkConfig.providers[n].type.toLowerCase() == "pause") {
              this.adsdkConfig.pauseAds.adList = this.adsdkConfig.providers[n];
            }
            n++;
          })

          if(appConfig?.env == 'uat' && appConfig.platform_id == PLATFORM.VIZIO) {
            this.adsdkConfig.providers = [this.adsdkConfig.providers[0]];
            this.adsdkConfig.providers[0].url = appConfig.testInfo.testVizioTag;
          }
          /*data.adsdk.adsdklist.forEach((key:any, value:any)=> {
            this.adsdkConfig.providers[n] = [];
            this.adsdkConfig.providers[n].name = value.name;
            this.adsdkConfig.providers[n].provider = value.provider;
            this.adsdkConfig.providers[n].priority = value.priority;
            this.adsdkConfig.providers[n].type = value.type;
            this.adsdkConfig.providers[n].url = value.url;

            if(value.type == "popup") {
              this.adsdkConfig.bugAds.adList = this.adsdkConfig.providers[n];
            }

            n++;
          });*/
        }
      }
    }
    // bug ads
    if (data.bugadenabled !== undefined && data.bugadenabled !== undefined) {
      this.adsdkConfig.bugAds.bugadenabled = data.bugadenabled;
      this.adsdkConfig.bugAds.firstbugadinterval = parseInt(data.firstbugadinterval);
      this.adsdkConfig.bugAds.bugadInterval = parseInt(data.bugadInterval);
      this.adsdkConfig.bugAds.bugadduration = parseInt(data.bugadduration);
      this.adsdkConfig.bugAds.bugadrepeatinterval = parseInt(data.bugadrepeatinterval);
      this.adsdkConfig.bugAds.bugadstartinterval = parseInt(data.bugadstartinterval);
    }
    // pause ads
    this.adsdkConfig.pauseAds.pauseadduration = PAUSE_AD_DEFAULT_DURATION
  }

  _getRootCategories(data: any): Channel[] {
    const { appConfig } = AppData as AppSetup
    let channelsData = data.channels
    let testwepb = (appConfig?.testWebp) || false
    if(!appConfig?.enableLiveRow){
      channelsData=channelsData.filter(item => item.actionkey != 'Live');
    }
    const channels = channelsData.map((item: any, index: number) => {
      
      const channel: Channel = {
        actionkey: item.actionkey,
        actiontype: item.actiontype,
        bannerad_image: item.bannerad_image,
        multiplebannerad_image: item.multiplebannerad_image,
        bg_image: (testwepb ? 'https://testing2.freeawesometv.com/new/resource/webp/UI/hero.webp' : item.bg_image),
        cg_image: (testwepb ? 'https://testing2.freeawesometv.com/new/resource/webp/UI/carousel.webp' : item.cg_image),
        blockItem: Helper.toBoolean(item.blockItem, false),
        category_id: item.category_id,
        content_type: item.content_type,
        description: item.description,
        feed_type: item.feed_type,
        name: item.name,
        url: item.url,
        isKids: item.isKids,
        isLivetv: item.isLivetv,
        root_icon: (testwepb && index==0) ? 'https://testing2.freeawesometv.com/new/resource/webp/UI/nav-home.webp': item.root_icon,
        root_icon_select: (testwepb && index==0) ? 'https://testing2.freeawesometv.com/new/resource/webp/UI/nav-home-on.webp': item.root_icon_select,
        moreBgImage: data.moreBgImage, // this image we can use for the mid carousel row.
        moreImage: data.moreImage,
        moreSelectImage: data.moreSelectImage,
        search_root_icon: testwepb ? 'https://testing2.freeawesometv.com/new/resource/webp/UI/nav-search.webp': data.search_root_icon,
        search_root_icon_select: testwepb ? 'https://testing2.freeawesometv.com/new/resource/webp/UI/nav-search-on.webp': data.search_root_icon_select
      }
      if (item.bannerad_image && item.bannerad_image !== '') {
        const linkEl = document.createElement('link')
        linkEl.rel = 'preload'
        linkEl.href = item.bannerad_image
        linkEl.as = 'image'
        document.head.appendChild(linkEl)
        //<link rel="preload" href="https://a8g4i9g5y.ssl.hwcdn.net/files/a8wn4hw/im/iptv/e3/b6/624d9e4b29ec6a33c1a8fecd7246c4a2.jpg" as="image">
      }
      return channel
    })
    return channels
  }

  _getSelectScreen(appdata: any): SelectScreenInfo | undefined {
    if (appdata.selectscreen && appdata.selectscreen.length > 0) return undefined
    const data: any = appdata.selectscreen
    const selectScreen: SelectScreenInfo = {
      focusimg: data.focusimg,
      fontcolor: data.fontcolor,
      liveTvFeedUrl: data.liveTvFeedUrl,
      liveTvImg: data.liveTvImg,
      optimgh: data.optimgh,
      optimgw: data.optimgw,
      options: this._getSelectScreenOptions(data),
      promodesc: data.promodesc,
      promotext: data.promotext,
      screenbgcolor: data.screenbgcolor,
      scrollimgleft: data.scrollimgleft,
      scrollimgright: data.scrollimgright,
      selectimg: data.selectimg,
      skipimg: data.skipimg,
      skipimgSelect: data.skipimgSelect,
      textFocusColor: data.textFocusColor,
      textSize: data.textSize,
      type: data.type,
      bgImg: data.bgImg,
      noOfRow: data.noOfRow
    }
    return selectScreen
  }

  _getSelectScreenOptions(data: any): SelectScreenOptionInfo[] {
    let options: SelectScreenOptionInfo[] = []
    if (data.options && data.options.length > 0) {
      options = data.options.map((item: any) => {
        const option: SelectScreenOptionInfo = {
          blockItem: item.blockItem == 'true' ? true : false,
          desc: item.desc,
          img: item.img,
          imgSelect: item.imgSelect,
          l1_mapping: item.l1_mapping,
          title: item.title,
          url: item.url,
        }
        return option
      })
    }
    return options
  }

  _getImageAdList(data: any): ImageAdListInfo {
    let imgurl = (data && data.imageAdList && data.imageAdList.imgAd) ? data.imageAdList.imgAd : ''
    let videoInterval = (data && data.imageAdList && data.imageAdList.videoInterval) ? data.imageAdList.videoInterval : 1
    if(imgurl == '') {
      imgurl = Utils.asset('/images/default-video-loading.jpg')
    }
    const imageAdList: ImageAdListInfo = {
      videoInterval: videoInterval,
      imgAd: imgurl,
    }
    return imageAdList
  }

  _getPlaylistStrategy(data: any): PLAYLIST_SWITCHING_STRATEGY {
    switch (data.playlistSwitchingStrategy) {
      case PLAYLIST_SWITCHING_STRATEGY.CONTINUES:
        return PLAYLIST_SWITCHING_STRATEGY.CONTINUES
      default:
        return PLAYLIST_SWITCHING_STRATEGY.DEFAULT
    }
  }

  getTitleFontFamily(): string {
    if(this.appConfig.genre.toLocaleLowerCase() == 'kids') {
        return 'SFPro-Black-Kids'
    } else {
        return 'SFPro-Black'
    }
  }
}
